import { CategoryStatus } from "../../domain/category.interface";

/**
 * SIMS-8634 — which payer categories may be offered, and how they read.
 *
 * AC4 and AC5 look contradictory and are not; they govern two DIFFERENT controls:
 *   AC4 — a retired category is not offered when editing        → the editor's chip picker (L424)
 *   AC5 — a retired category stays shown where already used,
 *         and still returns its payers when filtered            → the payer's chips + the list
 *                                                                 filter select (L127-131)
 *
 * The Epic's second read originally reported these as a contradiction by conflating the picker
 * with the filter (SR-2). Keeping them as two functions is what stops that confusion returning.
 *
 * The predicate is the Epic's shared one — "active OR already applied" — the same rule as the
 * program payer field (SIMS-8876), the member plan assignment (SIMS-8638) and the cup picker
 * (SIMS-8831). One rule, four screens.
 */

/** The shape needed here — a subset of the model's `CategoryItem`. */
export interface PayerCategory {
	_id: string;
	languages: { language: string; name: string }[];
	/** Optional on the model, so an unset status counts as active. */
	status?: CategoryStatus;
}

const isActive = (category: PayerCategory): boolean =>
	category.status !== CategoryStatus.INACTIVE;

/**
 * Categories the editor may offer (AC4 + AC5).
 *
 * An inactive category is offered only when the payer already carries it — otherwise the picker
 * could not render its own value, and the editor would silently drop it on save. That is the same
 * class of loss as SIMS-8876, where a program lost its inactive payer.
 */
export function selectableCategories<T extends PayerCategory>(
	all: T[],
	appliedIds: string[] | undefined | null,
): T[] {
	const applied = new Set(appliedIds ?? []);
	return all.filter(
		(category) => isActive(category) || applied.has(category._id),
	);
}

/**
 * Categories the LIST FILTER may offer (AC5).
 *
 * Every category, retired ones included: filtering by a retired category must still return the
 * payers carrying it, so hiding it from the filter would make those payers unreachable. This is
 * the control AC4 does not govern.
 */
export function filterableCategories<T extends PayerCategory>(all: T[]): T[] {
	return [...all];
}

/**
 * How a category reads (design L971).
 *
 * An inactive one is suffixed "(inactive)" rather than relying on the dashed border and 60%
 * opacity alone — NFR-5 forbids conveying state by appearance only.
 */
export function categoryLabel(category: PayerCategory): string {
	// Real data uses "enUS" (verified in dev), not "en" — matching only "en" would silently fall
	// through to whichever language happens to be first.
	const english = category.languages.find((l) =>
		l.language.toLowerCase().startsWith("en"),
	);
	const name = (english ?? category.languages[0])?.name ?? "Untitled category";
	return isActive(category) ? name : `${name} (inactive)`;
}
